/*
 * Product reels
 *
 * Full screen, reels style player for the product videos. The round thumbnails below the color option
 * (snippets/product-reels.liquid) open it; they are rendered inside <variant-selects>, so Liquid re-renders
 * them for every variant and the thumbnails present in the DOM are the videos to cycle through.
 *
 * Mirrors the theme's <popup-component> for opening/closing, scroll locking and focus trapping.
 */
(function () {
  'use strict';

  const selectors = {
    productInfo: 'product-info',
    variantSelects: 'variant-selects',
    dialog: 'dialog',
    open: '[data-reel-open]',
    close: '[data-reel-close]',
    prev: '[data-reel-prev]',
    next: '[data-reel-next]',
    mute: '[data-reel-mute]',
    card: '[data-reel-card]',
    slide: '[data-reel-slide]',
    video: '[data-reel-video]',
    externalTemplate: '[data-reel-external]',
    iframe: 'iframe',
    media: '.product-reels__media',
    stage: '[data-reel-stage]',
    thumbVideo: '[data-reel-thumb-video]',
  };

  const classes = {
    muted: 'is-muted',
    single: 'product-reels--single',
  };

  const attributes = {
    mediaId: 'data-media-id',
    optionValueId: 'data-option-value-id',
    closing: 'closing',
    labelMute: 'data-label-mute',
    labelUnmute: 'data-label-unmute',
  };

  const swipeThreshold = 50;

  class ProductReels extends HTMLElement {
    constructor() {
      super();

      this.dialog = this.querySelector(selectors.dialog);
      this.slides = [...this.querySelectorAll(selectors.slide)];
      this.container = this.closest(selectors.productInfo) || document;
      this.a11y = window.theme.a11y;
      this.muted = true;
      this.visibleIds = [];
      this.currentId = null;
      this.isAnimating = false;
      this.touchStart = null;

      this.onContainerClick = (event) => this.handleContainerClick(event);
      this.onDialogClick = (event) => this.handleDialogClick(event);
      this.onKeydown = (event) => this.handleKeydown(event);
      this.onTouchStart = (event) => this.handleTouchStart(event);
      this.onTouchEnd = (event) => this.handleTouchEnd(event);
      this.onClose = () => this.closeActions();
    }

    connectedCallback() {
      this.container.addEventListener('click', this.onContainerClick);
      this.dialog.addEventListener('click', this.onDialogClick);
      this.dialog.addEventListener('keydown', this.onKeydown);
      this.dialog.addEventListener('touchstart', this.onTouchStart, {passive: true});
      this.dialog.addEventListener('touchend', this.onTouchEnd, {passive: true});
      this.dialog.addEventListener('close', this.onClose);
    }

    disconnectedCallback() {
      this.container.removeEventListener('click', this.onContainerClick);
    }

    /*
     * Thumbnails
     */
    handleContainerClick(event) {
      const trigger = event.target.closest(selectors.open);
      if (!trigger || !this.container.contains(trigger)) return;

      event.preventDefault();
      this.open(trigger.getAttribute(attributes.mediaId), trigger);
    }

    getVisibleTriggers() {
      // The previous <variant-selects> stays in the DOM (hidden) for a moment after a variant change, skip its thumbnails
      return [...this.container.querySelectorAll(selectors.open)].filter((trigger) => trigger.offsetParent !== null);
    }

    getThumbVideos() {
      return [...this.container.querySelectorAll(selectors.thumbVideo)];
    }

    pauseThumbnails() {
      this.getThumbVideos().forEach((video) => video.pause());
    }

    resumeThumbnails() {
      this.getThumbVideos().forEach((video) => {
        if (video.offsetParent === null) return;
        video.play()?.catch(() => {});
      });
    }

    /*
     * Open / close
     */
    open(mediaId, trigger) {
      if (this.isAnimating || !this.dialog.hasAttribute('inert')) return;

      this.visibleIds = [...new Set(this.getVisibleTriggers().map((item) => item.getAttribute(attributes.mediaId)))];
      if (!this.visibleIds.length) {
        this.visibleIds = this.slides.map((slide) => slide.getAttribute(attributes.mediaId));
      }
      if (!this.visibleIds.includes(mediaId)) mediaId = this.visibleIds[0];

      this.classList.toggle(classes.single, this.visibleIds.length < 2);
      this.a11y.lastElement = trigger;
      this.pauseThumbnails();

      this.isAnimating = true;
      if (typeof this.dialog.showModal === 'function') {
        this.dialog.showModal();
      } else {
        this.dialog.setAttribute('open', '');
      }
      this.dialog.removeAttribute('inert');
      this.dialog.setAttribute('aria-hidden', false);
      document.dispatchEvent(new CustomEvent('theme:scroll:lock', {bubbles: true}));

      this.showSlide(mediaId);

      window.theme.waitForAnimationEnd(this.dialog).then(() => {
        this.isAnimating = false;
        this.a11y.trapFocus(this.dialog, {elementToFocus: this.querySelector(selectors.close)});
      });
    }

    close() {
      if (this.isAnimating || this.dialog.hasAttribute('inert')) return;

      // Play the closing animation first, then close for real
      if (!this.dialog.hasAttribute(attributes.closing)) {
        this.dialog.setAttribute(attributes.closing, '');
        this.isAnimating = true;

        window.theme.waitForAnimationEnd(this.dialog).then(() => {
          this.isAnimating = false;
          this.close();
        });

        return;
      }

      if (typeof this.dialog.close === 'function') {
        this.dialog.close();
      } else {
        this.dialog.removeAttribute('open');
        this.closeActions();
      }
    }

    closeActions() {
      if (this.dialog.hasAttribute('inert')) return;

      this.dialog.setAttribute('inert', '');
      this.dialog.setAttribute('aria-hidden', true);
      this.dialog.removeAttribute(attributes.closing);
      this.stopAllSlides();
      this.resumeThumbnails();

      if (!window.theme.hasOpenModals()) {
        document.dispatchEvent(new CustomEvent('theme:scroll:unlock', {bubbles: true}));
      }

      this.a11y.removeTrapFocus();
      this.a11y.autoFocusLastElement();
    }

    /*
     * Slides
     */
    showSlide(mediaId) {
      this.currentId = mediaId;

      this.slides.forEach((slide) => {
        const isCurrent = slide.getAttribute(attributes.mediaId) === mediaId;
        slide.hidden = !isCurrent;

        if (isCurrent) {
          this.playSlide(slide);
        } else {
          this.stopSlide(slide);
        }
      });
    }

    playSlide(slide) {
      const video = slide.querySelector(selectors.video);
      const template = slide.querySelector(selectors.externalTemplate);

      if (video) {
        video.muted = this.muted;
        video.currentTime = 0;
        video.play()?.catch(() => {});
      }

      // External videos (YouTube/Vimeo) are only embedded while shown
      if (template && !slide.querySelector(selectors.iframe)) {
        slide.querySelector(selectors.media).appendChild(template.content.cloneNode(true));
      }

      this.updateMuteButtons();
    }

    stopSlide(slide) {
      const video = slide.querySelector(selectors.video);
      if (video) video.pause();

      slide.querySelector(selectors.iframe)?.remove();
    }

    stopAllSlides() {
      this.slides.forEach((slide) => this.stopSlide(slide));
    }

    navigate(direction) {
      if (this.visibleIds.length < 2) return;

      const index = this.visibleIds.indexOf(this.currentId);
      const nextIndex = (index + direction + this.visibleIds.length) % this.visibleIds.length;

      this.showSlide(this.visibleIds[nextIndex]);
    }

    /*
     * Sound
     */
    toggleMute() {
      this.muted = !this.muted;

      this.slides.forEach((slide) => {
        const video = slide.querySelector(selectors.video);
        if (video) video.muted = this.muted;
      });

      this.updateMuteButtons();
    }

    updateMuteButtons() {
      this.classList.toggle(classes.muted, this.muted);

      this.querySelectorAll(selectors.mute).forEach((button) => {
        button.setAttribute('aria-label', button.getAttribute(this.muted ? attributes.labelUnmute : attributes.labelMute));
      });
    }

    /*
     * Product card: closes the player and selects the video's color
     */
    handleCardClick(card) {
      const optionValueId = card.getAttribute(attributes.optionValueId);
      this.close();

      if (!optionValueId) return;

      const variantSelects = [...this.container.querySelectorAll(selectors.variantSelects)].find((element) => element.style.display !== 'none');
      const input = variantSelects?.querySelector(`input[${attributes.optionValueId}="${optionValueId}"]`);
      if (!input || input.checked) return;

      input.checked = true;
      input.dispatchEvent(new Event('change', {bubbles: true}));
    }

    /*
     * Events
     */
    handleDialogClick(event) {
      if (event.target.closest(selectors.close)) {
        event.preventDefault();
        this.close();
      } else if (event.target.closest(selectors.prev)) {
        event.preventDefault();
        this.navigate(-1);
      } else if (event.target.closest(selectors.next)) {
        event.preventDefault();
        this.navigate(1);
      } else if (event.target.closest(selectors.mute)) {
        event.preventDefault();
        this.toggleMute();
      } else if (event.target.closest(selectors.card)) {
        event.preventDefault();
        this.handleCardClick(event.target.closest(selectors.card));
      } else if (event.target === this.dialog || event.target.matches(selectors.stage)) {
        this.close();
      }
    }

    handleKeydown(event) {
      switch (event.code) {
        case 'Escape':
          event.preventDefault();
          this.close();
          break;
        case 'ArrowLeft':
        case 'ArrowUp':
          event.preventDefault();
          this.navigate(-1);
          break;
        case 'ArrowRight':
        case 'ArrowDown':
          event.preventDefault();
          this.navigate(1);
          break;
      }
    }

    handleTouchStart(event) {
      const touch = event.changedTouches[0];
      this.touchStart = {x: touch.clientX, y: touch.clientY};
    }

    handleTouchEnd(event) {
      if (!this.touchStart || event.target.closest('button')) return;

      const touch = event.changedTouches[0];
      const deltaX = touch.clientX - this.touchStart.x;
      const deltaY = touch.clientY - this.touchStart.y;
      this.touchStart = null;

      // Swipe left/up for the next video, right/down for the previous one
      if (Math.abs(deltaX) >= swipeThreshold && Math.abs(deltaX) > Math.abs(deltaY)) {
        this.navigate(deltaX < 0 ? 1 : -1);
      } else if (Math.abs(deltaY) >= swipeThreshold) {
        this.navigate(deltaY < 0 ? 1 : -1);
      }
    }
  }

  customElements.get('product-reels') || customElements.define('product-reels', ProductReels);
})();
